import React, { useEffect, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'
import type { MultiSelectProps } from '@/features/enrollment'

export default function MultiSelect({ label, options, value, onChange, placeholder }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = React.useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = (option: string) => {
    if (value.includes(option)) {
      onChange(value.filter(item => item !== option))
    } else {
      onChange([...value, option])
    }
  }

  const handleRemove = (option: string) => {
    onChange(value.filter(item => item !== option))
  }

  const handleClearAll = () => {
    onChange([])
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-xs sm:text-sm font-medium text-accent-900 mb-2 sm:mb-3">{label}</label>
      <div className="relative">
        <div
          className="input-field w-full min-h-[40px] sm:min-h-[44px] flex items-center flex-wrap gap-1 sm:gap-2 p-2 cursor-pointer text-sm sm:text-base"
          onClick={() => setIsOpen(!isOpen)}
        >
          {value.length > 0 ? (
            <>
              {value.map((item) => (
                <span
                  key={item}
                  className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 px-1 sm:px-2 py-1 rounded text-xs sm:text-sm"
                >
                  {item}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleRemove(item)
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </>
          ) : (
            <span className="text-gray-500 text-sm sm:text-base">{placeholder || 'Select options...'}</span>
          )}
          <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 sm:max-h-60 overflow-auto">
            {options.map((option) => (
              <div
                key={option}
                className={`px-2 sm:px-3 py-2 cursor-pointer hover:bg-blue-50 text-sm sm:text-base ${
                  value.includes(option) ? 'bg-blue-100' : ''
                }`}
                onClick={() => handleSelect(option)}
              >
                {option}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}